import React, { useRef } from 'react';
import { Header, Icon, Checkbox, Button, MeetingId } from '@/components';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { history, useParams, useIntl, getLocale } from 'umi';
// import classnames from 'classnames';
import classnames from 'classnames/bind';
import { inject, observer } from 'mobx-react';
import RTCModel from '@/models/rtc';
import MeetingModel from '@/models/meeting';
import UserModel from '@/models/user';
import { durationTime } from '@/utils/common';
import { useMount } from '@umijs/hooks';
import lottie, { AnimationItem } from 'lottie-web';
import { TIPS_TYPE } from '@/types/tips';
import { Question } from '@/types/meeting';
import Style from './index.less';

const $c = classnames.bind(Style);

// 根据设备类型判断屏幕共享的筛选项
function filterScreenOption(opt: Question) {
  if (!isPad && !isMobile) return true;
  if (opt.type !== 3) {
    return true;
  }
  if ([203, 207, 209, 211].includes(opt.id)) {
    return true;
  }
  return false;
}

interface FeedbackProps {
  RTCInstance: RTCModel;
  meeting: MeetingModel;
  user: UserModel;
}
interface ParamsStruct {
  meetingId: string;
}

const Feedback: React.FC<FeedbackProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer(props => {
    const { RTCInstance, meeting, user } = props;
    const { meetingId } = useParams<ParamsStruct>();
    const [optionsType, setOptonsType] = React.useState(2);
    const [checkboxValuesMap, setCheckboxValuesMap] = React.useState<Map<number, boolean>>(new Map());
    const [feedbackVisible, setFeedbackVisible] = React.useState(false);
    const [remark, setRemark] = React.useState('');
    const { formatMessage } = useIntl();
    const manyiRef = useRef<HTMLDivElement>(null);
    const bumanyiRef = useRef<HTMLDivElement>(null);
    const feedbackManyiRef = useRef<HTMLDivElement>(null);
    const [manyiLottie, setManyiLottie] = React.useState<AnimationItem | null>(null);
    const [bumanyiLottie, setBumanyiLottie] = React.useState<AnimationItem | null>(null);
    const [feedbackManyiLottie, setFeedbackManyiLottie] = React.useState<AnimationItem | null>(null);

    const onCheckboxChange = (e: CheckboxChangeEvent) => {
      const map = new Map();
      console.log(e, 'e');
      map.set(e.target.value, e.target.checked);
      setCheckboxValuesMap(new Map([...checkboxValuesMap, ...map]));
    };

    const btnDisabled = React.useMemo(() => {
      return ![...checkboxValuesMap.values()].some(item => !!item);
    }, [checkboxValuesMap]);

    const showFeedbackOptions = React.useCallback(() => {
      setFeedbackVisible(!feedbackVisible);
    }, [feedbackVisible]);

    const onTextareaChange = React.useCallback(e => {
      const { value } = e.target;
      let len = 0;
      for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i);
        if (charCode >= 0 && charCode <= 128) len += 1;
        else len += 2;
      }
      if (len <= 400) {
        setRemark(value);
      }
    }, []);

    const submit = React.useCallback(
      async (isGreat: boolean) => {
        try {
          if (isGreat) {
            await user.hangUp(meetingId, RTCInstance.cid, user.uid, isGreat);
          } else {
            const ids: number[] = [];
            [...checkboxValuesMap.keys()].forEach((key: number) => {
              if (checkboxValuesMap.get(key)) {
                ids.push(Number(key));
              }
            });
            await user.hangUp(meetingId, RTCInstance.cid, user.uid, isGreat, ids, remark);
          }
          history.push({
            pathname: 'leave',
            query: {
              type: 'improve',
            },
          });
        } catch (err) {
          console.log('提交反馈失败', err);
          throw err;
        }
      },
      [user, checkboxValuesMap, remark, meetingId],
    );

    useMount(async () => {
      if (user.uid && RTCInstance.cid) {
        await meeting.getFeedbackQuestion(meetingId, RTCInstance.cid, user.uid, getLocale(), 0);
      } else {
        history.push({
          pathname: `/meet/${meetingId}/tips`,
          query: {
            type: TIPS_TYPE.NOT_FOUND,
          },
        });
      }
    });

    React.useEffect(() => {
      if (manyiRef && manyiRef.current) {
        const manyi = lottie.loadAnimation({
          container: manyiRef.current, // the dom element that will contain the animation
          renderer: 'svg',
          loop: true,
          autoplay: false,
          path: 'https://popo.fp.ps.netease.com/file/603320575e602727761cdd2cKUkIEwEs03.json', // the path to the animation json
        });
        setManyiLottie(manyi);
      }
      if (bumanyiRef && bumanyiRef.current) {
        const bumanyi = lottie.loadAnimation({
          container: bumanyiRef.current, // the dom element that will contain the animation
          renderer: 'svg',
          loop: true,
          autoplay: false,
          path: 'https://popo.fp.ps.netease.com/file/603321016f04941b4dd243b0GWs9GNGN03.json', // the path to the animation json
        });
        setBumanyiLottie(bumanyi);
      }
      if (feedbackManyiRef && feedbackManyiRef.current) {
        const feedbackManyi = lottie.loadAnimation({
          container: feedbackManyiRef.current, // the dom element that will contain the animation
          renderer: 'svg',
          loop: true,
          autoplay: false,
          path: 'https://popo.fp.ps.netease.com/file/603320575e602727761cdd2cKUkIEwEs03.json', // the path to the animation json
        });
        setFeedbackManyiLottie(feedbackManyi);
      }
    }, [manyiRef, bumanyiRef, feedbackManyiRef]);

    const time = React.useMemo(() => {
      const now = new Date().getTime();
      return durationTime(now, meeting.info?.createtime || 0);
    }, [meeting.info?.createtime]);

    const onFeedbackManyiMouseEnter = React.useCallback(() => {
      feedbackManyiLottie && feedbackManyiLottie.play();
    }, [feedbackManyiLottie]);

    const title = React.useMemo(() => {
      if (feedbackVisible) {
        return formatMessage({ id: 'ISSUES' });
      }
      return formatMessage({ id: 'MEETING_ENDING' });
    }, [feedbackVisible]);

    return (
      <div className={Style['popo-meeting-feedback-page']}>
        <h1 className={Style.title}>· {title} ·</h1>
        <div className={Style.content}>
          <div className={classnames(Style['info-panel'], { [Style['no-visible']]: feedbackVisible })}>
            <p className={Style['meeting-name']}>{meeting.title}</p>
            <span className={Style['meeting-number']}>
              {formatMessage({ id: 'CONFERENCE_NUMBER' })}：
              <MeetingId meetingId={meeting.info?.meetingId || 0} />
            </span>
            <span className={Style['meeting-time']}>
              {formatMessage({ id: 'DURATION' })}：{time}
            </span>
            <span className={Style.line} />
            <p className={Style.desc}>{formatMessage({ id: 'EXPERIENCE' })}</p>
            <div className={Style['btn-groups']}>
              <div
                className={Style.btn}
                onClick={async () => {
                  await submit(true);
                }}
                onMouseEnter={() => {
                  manyiLottie && manyiLottie.play();
                }}
                onMouseLeave={() => {
                  manyiLottie && manyiLottie.stop();
                }}
              >
                <div ref={manyiRef} style={{ height: '28px', width: '28px' }} />
                <span className={Style.text}>{formatMessage({ id: 'GREAT' })}</span>
              </div>
              <div
                className={Style.btn}
                onClick={showFeedbackOptions}
                onMouseEnter={() => {
                  bumanyiLottie && bumanyiLottie.play();
                }}
                onMouseLeave={() => {
                  bumanyiLottie && bumanyiLottie.stop();
                }}
              >
                <div ref={bumanyiRef} style={{ height: '28px', width: '28px' }} />
                <span className={Style.text}>{formatMessage({ id: 'ISSUES' })}</span>
              </div>
            </div>
          </div>
          <div className={classnames(Style['feedback-panel'], { [Style.visible]: feedbackVisible })}>
            <div className={Style['manyi-wrap']} onMouseEnter={onFeedbackManyiMouseEnter}>
              <div
                className={Style['manyi-box']}
                onClick={async () => {
                  await submit(true);
                }}
              >
                {formatMessage({ id: 'GREAT' })}
              </div>
              <div className={Style['feedback-manyi']} ref={feedbackManyiRef} />
              <img className={Style.manyi} src={require('../../assets/manyi.png')} alt="" />
            </div>
            <div className={$c('options-change')}>
              <span className={$c('change-item', { active: optionsType === 2 })} onClick={() => setOptonsType(2)}>
                音频视频
              </span>
              <span className={$c('change-item', { active: optionsType === 3 })} onClick={() => setOptonsType(3)}>
                屏幕共享
              </span>
            </div>
            <div className={$c('options', { 'options-hide': optionsType !== 2 })}>
              {meeting.questions
                .filter(n => n.type === 2)
                .map((item, key) => {
                  return (
                    <div
                      className={classnames(`${Style['option-item']} needsclick`, {
                        [Style['option-checked']]: checkboxValuesMap.get(item.id),
                      })}
                      key={key}
                    >
                      <Checkbox
                        className={`${Style['checkbox-item']} needsclick`}
                        value={item.id}
                        onChange={onCheckboxChange}
                      >
                        {item.doc}
                      </Checkbox>
                    </div>
                  );
                })}
            </div>
            <div className={$c('options', { 'options-hide': optionsType !== 3 })}>
              {meeting.questions
                .filter(n => n.type === 3 && filterScreenOption(n))
                .map((item, key) => {
                  return (
                    <div
                      className={classnames(`${Style['option-item']} needsclick`, {
                        [Style['option-checked']]: checkboxValuesMap.get(item.id),
                      })}
                      key={key}
                    >
                      <Checkbox
                        className={`${Style['checkbox-item']} needsclick`}
                        value={item.id}
                        onChange={onCheckboxChange}
                      >
                        {item.doc}
                      </Checkbox>
                    </div>
                  );
                })}
            </div>
            <div className={Style['textarea-wrap']}>
              <textarea
                value={remark}
                placeholder={formatMessage({ id: 'OTHER_FEEDBACK' })}
                onChange={onTextareaChange}
              />
            </div>
            <Button
              onClick={async () => {
                await submit(false);
              }}
              className={Style.submit}
              type="primary"
            >
              {formatMessage({ id: 'SUBMIT' })}
            </Button>
            {!window.isMobile && <img className={Style.bumanyi} src={require('../../assets/bumanyi.png')} alt="" />}
          </div>
        </div>
      </div>
    );
  }),
);

export default Feedback;
